import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Shell from "@/components/layout/Shell";
import { api } from "@/lib/api";
import { PageHeader, PrimaryButton, GhostButton, StatusBadge, formatCOP, Field, inputCls } from "@/components/common";
import { ArrowLeft, Copy, ExternalLink, RefreshCw, Mail, Phone, Store, Edit3, Users as UsersIcon, Trash2, Upload, ImageOff, FolderKanban, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

import { StatusPill as ProjectStatusPill, ProgressBar } from "@/components/projectShared";

const MAX_LOGO_BYTES = 700 * 1024;

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${id}`);
      setData(data);
      setForm({ ...data.client });
    } catch { toast.error("Cliente no encontrado"); navigate("/clientes"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error("Nombre requerido"); return; }
    try {
      await api.put(`/clients/${id}`, {
        name: form.name, contact_name: form.contact_name || "", email: form.email || "",
        phone: form.phone || "", service: form.service, status: form.status,
        billing_type: form.billing_type || "mensual",
        monthly_value: Number(form.monthly_value) || 0, notes: form.notes || "",
        nit: form.nit || "", legal_rep: form.legal_rep || "", legal_rep_id: form.legal_rep_id || "", address: form.address || "",
        churn_reason: form.churn_reason || "",
      });
      toast.success("Cliente actualizado"); setEditOpen(false); load();
    } catch { toast.error("No se pudo guardar"); }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar el cliente "${data.client.name}"?`)) return;
    await api.delete(`/clients/${id}`);
    toast.success("Cliente eliminado"); navigate("/clientes");
  };

  const copyPortalLink = () => {
    const url = `${window.location.origin}/portal/${data.client.portal_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Enlace copiado al portapapeles");
  };

  const openPortal = () => {
    window.open(`/portal/${data.client.portal_token}`, "_blank");
  };

  const rotateToken = async () => {
    if (!window.confirm("¿Generar un nuevo enlace? El enlace actual dejará de funcionar.")) return;
    await api.post(`/clients/${id}/rotate-portal-token`);
    toast.success("Nuevo enlace generado"); load();
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Debe ser una imagen"); return; }
    if (file.size > MAX_LOGO_BYTES) { toast.error("Máximo 700KB. Comprime la imagen."); return; }
    setUploadingLogo(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await api.post(`/clients/${id}/logo`, { logo_data_url: dataUrl });
      toast.success("Logo actualizado");
      load();
    } catch { toast.error("No se pudo subir el logo"); }
    finally { setUploadingLogo(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm("¿Quitar el logo?")) return;
    await api.delete(`/clients/${id}/logo`);
    toast.success("Logo eliminado"); load();
  };

  const invoiceNow = async () => {
    const cli = data.client;
    if (!cli.email && !window.confirm("Este cliente no tiene email. Se creará la factura pero no se enviará. ¿Continuar?")) return;
    if (cli.email && !window.confirm(`Se creará una factura por ${new Intl.NumberFormat("es-CO",{style:"currency",currency:"COP",maximumFractionDigits:0}).format(cli.monthly_value)} y se enviará a ${cli.email}. ¿Continuar?`)) return;
    try {
      const { data: res } = await api.post(`/clients/${id}/invoice-now`);
      if (res.emailed) toast.success(`Factura ${res.number} creada y enviada`);
      else if (!res.client_has_email) toast.success(`Factura ${res.number} creada (sin email)`);
      else toast.error(`Factura ${res.number} creada, pero el email falló`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo emitir la factura");
    }
  };

  if (loading || !data) return <Shell><div className="text-[#9CA3AF]">Cargando…</div></Shell>;

  const { client, appointments, invoices, team, projects = [] } = data;
  const portalUrl = `${window.location.origin}/portal/${client.portal_token}`;
  const totalPaid = invoices.filter(i => i.status === "cobrada").reduce((s,i)=>s+i.amount, 0);
  const totalPending = invoices.filter(i => i.status !== "cobrada").reduce((s,i)=>s+i.amount, 0);

  return (
    <Shell>
      <button
        data-testid="back-to-clients"
        onClick={()=>navigate("/clientes")}
        className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-[#F5F5F7] mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.75}/> Volver a Clientes
      </button>

      <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="relative group">
              {client.logo_url ? (
                <img src={client.logo_url} alt={client.name} className="w-16 h-16 rounded-2xl object-cover border border-[#262A33]"/>
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-[#F97316]/10 flex items-center justify-center text-[#F97316] text-2xl font-bold">
                  {client.name.charAt(0).toUpperCase()}
                </div>
              )}
              <label
                data-testid="upload-logo-btn"
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#F97316] text-white flex items-center justify-center cursor-pointer hover:bg-[#EA580C] transition-colors shadow-lg"
                title="Subir logo"
              >
                <Upload className="w-3 h-3" strokeWidth={2}/>
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploadingLogo}/>
              </label>
              {client.logo_url && (
                <button
                  data-testid="remove-logo-btn"
                  onClick={removeLogo}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#15171E] border border-[#262A33] text-red-400 flex items-center justify-center hover:bg-red-500/10 transition-colors"
                  title="Quitar logo"
                >
                  <ImageOff className="w-2.5 h-2.5" strokeWidth={2}/>
                </button>
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#F5F5F7] tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
                {client.name}
              </h1>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <StatusBadge status={client.status}/>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${
                  client.billing_type === "unico"
                    ? "bg-purple-500/10 text-purple-300 border-purple-500/20"
                    : "bg-[#F97316]/10 text-[#F97316] border-[#F97316]/20"
                }`}>
                  {client.billing_type === "unico" ? "Pago único" : "Mensual"}
                </span>
                <span className="text-xs text-[#9CA3AF] capitalize">{client.service}</span>
                <span className="text-xs text-[#F97316] font-medium">
                  {formatCOP(client.monthly_value)}
                  {client.billing_type !== "unico" && "/mes"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {client.billing_type === "unico" && (
              <PrimaryButton data-testid="invoice-now-btn" onClick={invoiceNow}>
                <Zap className="w-4 h-4"/> Cobrar ahora
              </PrimaryButton>
            )}
            <GhostButton data-testid="edit-client-btn" onClick={()=>setEditOpen(true)}><Edit3 className="w-4 h-4"/> Editar</GhostButton>
            <button onClick={remove} className="inline-flex items-center gap-2 bg-transparent border border-red-500/30 hover:bg-red-500/10 text-red-400 font-medium rounded-xl px-3 py-2 text-sm transition-colors" data-testid="delete-client-btn">
              <Trash2 className="w-4 h-4"/> Eliminar
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-4">
            <div className="text-xs text-[#9CA3AF] mb-1">Contacto</div>
            <div className="text-sm text-[#F5F5F7]">{client.contact_name || "—"}</div>
          </div>
          <div className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-4">
            <div className="text-xs text-[#9CA3AF] mb-1 flex items-center gap-1"><Mail className="w-3 h-3"/> Email</div>
            <div className="text-sm text-[#F5F5F7] break-all">{client.email || "—"}</div>
          </div>
          <div className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-4">
            <div className="text-xs text-[#9CA3AF] mb-1 flex items-center gap-1"><Phone className="w-3 h-3"/> Teléfono</div>
            <div className="text-sm text-[#F5F5F7]">{client.phone || "—"}</div>
          </div>
        </div>

        {client.notes && (
          <div className="mt-4 bg-[#0B0D12] border border-[#262A33] rounded-xl p-4">
            <div className="text-xs text-[#9CA3AF] mb-1">Notas internas</div>
            <div className="text-sm text-[#F5F5F7] whitespace-pre-wrap">{client.notes}</div>
          </div>
        )}
      </div>

      {/* Portal privado */}
      <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 mb-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>Portal privado del cliente</h3>
            <p className="text-xs text-[#9CA3AF]">Comparte este enlace único con el cliente para que consulte sus facturas y próximas reuniones sin necesidad de llamarte.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-[#0B0D12] border border-[#262A33] rounded-xl px-3 py-2">
          <input readOnly value={portalUrl} data-testid="portal-url-input" className="flex-1 bg-transparent text-xs text-[#9CA3AF] outline-none font-mono truncate"/>
          <button onClick={copyPortalLink} data-testid="copy-portal-btn" className="p-1.5 rounded-lg hover:bg-[#262A33] text-[#F97316] transition-colors" aria-label="Copiar enlace"><Copy className="w-4 h-4" strokeWidth={1.75}/></button>
          <button onClick={openPortal} data-testid="open-portal-btn" className="p-1.5 rounded-lg hover:bg-[#262A33] text-[#F5F5F7] transition-colors" aria-label="Abrir"><ExternalLink className="w-4 h-4" strokeWidth={1.75}/></button>
          <button onClick={rotateToken} data-testid="rotate-portal-btn" className="p-1.5 rounded-lg hover:bg-[#262A33] text-[#9CA3AF] transition-colors" aria-label="Regenerar"><RefreshCw className="w-4 h-4" strokeWidth={1.75}/></button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5">
          <div className="text-xs text-[#9CA3AF] mb-1">Total cobrado</div>
          <div className="text-xl font-bold text-emerald-400" style={{ fontFamily: "Manrope, sans-serif" }}>{formatCOP(totalPaid)}</div>
        </div>
        <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5">
          <div className="text-xs text-[#9CA3AF] mb-1">Pendiente</div>
          <div className="text-xl font-bold text-amber-400" style={{ fontFamily: "Manrope, sans-serif" }}>{formatCOP(totalPending)}</div>
        </div>
        <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5">
          <div className="text-xs text-[#9CA3AF] mb-1">Reuniones registradas</div>
          <div className="text-xl font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>{appointments.length}</div>
        </div>
      </div>

      {/* Proyectos */}
      <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-[#F5F5F7] flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <FolderKanban className="w-4 h-4 text-[#F97316]" strokeWidth={1.75}/> Proyectos
          </h3>
          <GhostButton data-testid="add-project-from-client" onClick={()=>navigate(`/proyectos?client=${id}`)}>Ver todos</GhostButton>
        </div>
        {projects.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">Sin proyectos activos con este cliente.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {projects.map(pr => {
              const isMensual = client.billing_type !== "unico";
              return (
              <button
                key={pr.project_id}
                data-testid={`client-project-${pr.project_id}`}
                onClick={()=>navigate(`/proyectos/${pr.project_id}`)}
                className={`text-left border rounded-xl p-4 hover:bg-[#1A1D24] transition-colors ${
                  isMensual ? "bg-[#0B0D12] border-[#F97316]/25 hover:border-[#F97316]/60" : "bg-[#0B0D12] border-purple-500/25 hover:border-purple-400/60"
                }`}
              >
                <div className={`text-[9px] uppercase font-semibold tracking-wide mb-1 ${isMensual ? "text-[#F97316]" : "text-purple-400"}`}>
                  {isMensual ? "Consultoría continua" : "Entrega única"}
                </div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="text-sm text-[#F5F5F7] font-semibold" style={{ fontFamily: "Manrope, sans-serif" }}>{pr.name}</div>
                  <ProjectStatusPill status={pr.status}/>
                </div>
                <div className="flex items-center gap-2">
                  <ProgressBar value={pr.progress} className="flex-1"/>
                  <span className="text-xs text-[#F97316] font-semibold w-9 text-right">{pr.progress}%</span>
                </div>
                <div className="text-[11px] text-[#9CA3AF] mt-2">{pr.tasks_done}/{pr.task_count} tareas{pr.due_date ? ` · Entrega ${pr.due_date}` : ""}</div>
              </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Citas */}
      <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 mb-6">
        <h3 className="font-bold text-[#F5F5F7] mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>Historial de citas</h3>
        {appointments.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">Aún no hay citas registradas con este cliente.</p>
        ) : (
          <div className="space-y-2">
            {appointments.map(a => {
              const d = new Date(a.scheduled_at);
              return (
                <div key={a.appointment_id} className="flex items-center gap-4 p-3 rounded-xl bg-[#0B0D12] border border-[#262A33]">
                  <div className="text-center w-12">
                    <div className="text-[10px] text-[#F97316] font-semibold uppercase">{d.toLocaleString("es-CO",{month:"short"})}</div>
                    <div className="text-lg text-[#F5F5F7] font-bold leading-tight">{d.getDate()}</div>
                    <div className="text-[10px] text-[#9CA3AF]">{d.toLocaleTimeString("es-CO",{hour:"2-digit",minute:"2-digit"})}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#F5F5F7] font-medium">{a.title}</div>
                    <div className="text-xs text-[#9CA3AF] capitalize">{a.meeting_type}</div>
                  </div>
                  <StatusBadge status={a.status}/>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Facturas */}
      <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 mb-6">
        <h3 className="font-bold text-[#F5F5F7] mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>Facturas</h3>
        {invoices.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">Sin facturas emitidas.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#9CA3AF] border-b border-[#262A33]">
                  <th className="text-left font-medium py-2">Número</th>
                  <th className="text-left font-medium py-2">Emitida</th>
                  <th className="text-left font-medium py-2">Vence</th>
                  <th className="text-left font-medium py-2">Estado</th>
                  <th className="text-right font-medium py-2">Monto</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(i => (
                  <tr key={i.invoice_id} className="border-b border-[#262A33] last:border-0">
                    <td className="py-2 text-[#F5F5F7] font-mono text-xs">{i.number}</td>
                    <td className="py-2 text-[#9CA3AF]">{i.issued_at}</td>
                    <td className="py-2 text-[#9CA3AF]">{i.due_at}</td>
                    <td className="py-2"><StatusBadge status={i.status}/></td>
                    <td className="py-2 text-right text-[#F5F5F7] font-medium">{formatCOP(i.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Equipo asignado */}
      {team && team.length > 0 && (
        <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 mb-6">
          <h3 className="font-bold text-[#F5F5F7] mb-4 flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <UsersIcon className="w-4 h-4 text-[#F97316]" strokeWidth={1.75}/> Equipo asignado
          </h3>
          <div className="flex flex-wrap gap-3">
            {team.map(t => (
              <div key={t.member_id} className="flex items-center gap-2 bg-[#0B0D12] border border-[#262A33] rounded-xl px-3 py-2">
                {t.photo_url ? <img src={t.photo_url} alt={t.name} className="w-7 h-7 rounded-full object-cover"/> : <div className="w-7 h-7 rounded-full bg-[#262A33]"/>}
                <div>
                  <div className="text-xs text-[#F5F5F7] font-medium">{t.name}</div>
                  <div className="text-[10px] text-[#F97316]">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal edit */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#15171E] border-[#262A33] text-[#F5F5F7] max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>Editar cliente</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Nombre *"><input required className={inputCls} value={form.name || ""} onChange={e=>setForm({...form, name: e.target.value})}/></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Persona de contacto"><input className={inputCls} value={form.contact_name || ""} onChange={e=>setForm({...form, contact_name: e.target.value})}/></Field>
              <Field label="Teléfono"><input className={inputCls} value={form.phone || ""} onChange={e=>setForm({...form, phone: e.target.value})}/></Field>
            </div>
            <Field label="Email"><input type="email" className={inputCls} value={form.email || ""} onChange={e=>setForm({...form, email: e.target.value})}/></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="NIT"><input data-testid="edit-client-nit" className={inputCls} value={form.nit || ""} onChange={e=>setForm({...form, nit: e.target.value})}/></Field>
              <Field label="Representante legal"><input className={inputCls} value={form.legal_rep || ""} onChange={e=>setForm({...form, legal_rep: e.target.value})}/></Field>
              <Field label="Cédula rep. legal"><input className={inputCls} value={form.legal_rep_id || ""} onChange={e=>setForm({...form, legal_rep_id: e.target.value})}/></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Servicio">
                <select className={inputCls} value={form.service || "marketing"} onChange={e=>setForm({...form, service: e.target.value})}>
                  <option value="marketing">Marketing digital</option>
                  <option value="consultoria">Consultoría</option>
                  <option value="ambos">Ambos</option>
                </select>
              </Field>
              <Field label="Estado">
                <select className={inputCls} value={form.status || "activo"} onChange={e=>setForm({...form, status: e.target.value})}>
                  <option value="activo">Activo</option>
                  <option value="pausado">Pausado</option>
                  <option value="finalizado">Finalizado</option>
                </select>
              </Field>
            </div>
            <Field label="Tipo de cobro">
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  data-testid="edit-billing-mensual-btn"
                  onClick={()=>setForm({...form, billing_type: "mensual"})}
                  className={`px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
                    (form.billing_type || "mensual") === "mensual"
                      ? "bg-[#F97316]/10 border-[#F97316] text-[#F97316]"
                      : "bg-[#0B0D12] border-[#262A33] text-[#9CA3AF] hover:text-[#F5F5F7]"
                  }`}
                >
                  Mensual (retainer)
                </button>
                <button
                  type="button"
                  data-testid="edit-billing-unico-btn"
                  onClick={()=>setForm({...form, billing_type: "unico"})}
                  className={`px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
                    form.billing_type === "unico"
                      ? "bg-purple-500/10 border-purple-500 text-purple-300"
                      : "bg-[#0B0D12] border-[#262A33] text-[#9CA3AF] hover:text-[#F5F5F7]"
                  }`}
                >
                  Pago único
                </button>
              </div>
            </Field>
            <Field label={form.billing_type === "unico" ? "Valor del proyecto (COP)" : "Valor mensual (COP)"}><input type="number" min="0" className={inputCls} value={form.monthly_value || 0} onChange={e=>setForm({...form, monthly_value: e.target.value})}/></Field>
            <Field label="Notas internas"><textarea rows={3} className={inputCls} value={form.notes || ""} onChange={e=>setForm({...form, notes: e.target.value})}/></Field>
            <DialogFooter>
              <GhostButton type="button" onClick={()=>setEditOpen(false)}>Cancelar</GhostButton>
              <PrimaryButton type="submit" data-testid="save-client-edit-btn">Guardar</PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
